/**
 * ABConvert price A/B test — DISPLAY ONLY.
 *
 * Rewrites the prices Horizon rendered so they match what ABConvert's cart
 * transform will actually charge. Implements ABConvert's documented
 * "Shopify function price test customization guide" (Kelly, 2026-05-26).
 *
 * ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * ABConvert's own script updates the product page but not product cards. Their
 * lookup is keyed on product id AND variant id, and on a card this theme shows
 * the CHEAPEST AVAILABLE variant (see snippets/price.liquid, "CARD PRICE
 * SOURCE") — which on the kit products is the Single, while the product's
 * default variant is the Full CrewCup Kit. Two different variants, and nothing
 * in the markup said which one the card's number came from.
 *
 * So the theme now emits data-ab-product-id / data-ab-variant-id next to every
 * price, and this reads them.
 *
 * ─── THE ONE THING TO UNDERSTAND BEFORE EDITING ─────────────────────────────
 *
 * This script does NOT set the price anyone is charged. ABConvert's cart
 * transform does that, server-side, and it will do it whether or not this file
 * runs. This only makes the DISPLAYED number agree with it.
 *
 * That fixes the failure direction. If this script does nothing — no test
 * running, storage blocked, their object absent, a shape we do not recognise —
 * the page keeps the price Liquid rendered, which is exactly today's behaviour.
 * Every unknown below therefore bails rather than guessing: a wrong number here
 * means a shopper is quoted one price and charged another, which is worse than
 * a stale one.
 *
 * ─── KILL SWITCH ────────────────────────────────────────────────────────────
 *
 * Remove the {% render 'abconvert-price-display' %} line from
 * layout/theme.liquid. Nothing else depends on this file. The data attributes
 * and .ab-* classes are inert on their own.
 */
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * One group's prices, in the shape ABConvert's guide documents. Everything is
 * optional because it is a third party's payload — the code treats every field
 * as absent until proven otherwise.
 * @typedef {object} AbPriceData
 * @property {string | number} [price]
 * @property {string | number | null} [compareAtPrice]
 * @property {Record<string, AbPriceData>} [countryMap] - Per-market prices, keyed by country code.
 */

/**
 * One product's entry in their dict.
 * @typedef {object} AbEntry
 * @property {string} [experimentId]
 * @property {Record<string, Record<string, AbPriceData>>} [variantToGroupIndexToPrice] - variant id -> group index -> prices.
 */

/**
 * @typedef {object} AbConfig
 * @property {string} moneyFormat - shop.money_format, e.g. '${{amount}}'.
 * @property {string} currency - What the page is displaying, e.g. 'PHP'.
 * @property {string} [primaryCurrency] - The shop's own currency, which their top-level price is in.
 * @property {string} [country] - The shopper's country, for countryMap.
 */

/**
 * @typedef {Window & typeof globalThis & {
 *   __crewcupAbPriceDisplay?: boolean,
 *   abconvertPriceCartTransformTestDict?: Record<string, AbEntry>,
 *   crewcupAbPriceDisplay?: { apply: () => boolean, debug: () => object },
 * }} AbWindow
 */

/** @type {AbWindow} */
const win = /** @type {AbWindow} */ (window);

if (!win.__crewcupAbPriceDisplay) {
  win.__crewcupAbPriceDisplay = true;

  const GROUP_KEY = 'abconvert-experiment-to-group-index';

  /* ------------------------------------------------------------------ config */

  /**
   * Money format comes from Liquid via a JSON block rather than being
   * hardcoded, so the output matches the `| money` filter exactly — same
   * symbol, same separators, same precision. Guessing `$${price}` (as their
   * guide's example does) would render "$1400" where the theme renders
   * "₱1,400.00".
   */
  /** @returns {AbConfig | null} */
  const readConfig = () => {
    const el = document.getElementById('crewcup-ab-price-config');
    if (!el?.textContent) return null;
    try {
      const parsed = JSON.parse(el.textContent);
      if (!parsed?.moneyFormat || !parsed?.currency) return null;
      return /** @type {AbConfig} */ (parsed);
    } catch {
      return null;
    }
  };

  const config = readConfig();

  /* ------------------------------------------------------- group assignment */

  /**
   * Storage may throw, not merely return null: private windows, blocked
   * cookies, the theme editor's cross-site iframe. Fail CLOSED here — the
   * opposite of crewcup-promo-popup.js — because without the group we cannot
   * know which price to show, and showing the wrong one misquotes a shopper.
   */
  /** @returns {Record<string, unknown> | null} */
  const readGroups = () => {
    try {
      const raw = win.localStorage?.getItem(GROUP_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };

  /**
   * Group index is a number in live tests and a STRING in preview mode — their
   * guide documents both. Object keys are always strings, so normalise to
   * string and reject anything that is not a plain integer index.
   */
  /**
   * @param {unknown} value - The raw group index from localStorage.
   * @returns {string | null} A string key for their price dict, or null if unusable.
   */
  const normaliseGroupIndex = (value) => {
    if (typeof value === 'number' && Number.isInteger(value)) return String(value);
    if (typeof value === 'string' && /^\d+$/.test(value.trim())) return value.trim();
    return null;
  };

  /* --------------------------------------------------------------- lookup */

  /**
   * The experiment id is read back OUT of their price dict rather than
   * hardcoded (their guide's example hardcodes it). Hardcoding would silently
   * stop working the next time the boss starts a new test, and silently is the
   * failure mode we can least afford — the page would keep quoting the old
   * price against a live transform.
   */
  /**
   * @param {string} productId
   * @param {string} variantId
   * @param {Record<string, unknown>} groups - Experiment id -> group index, from localStorage.
   * @returns {AbPriceData | null} Prices safe to display, or null to leave the page alone.
   */
  const priceFor = (productId, variantId, groups) => {
    if (!config) return null;

    const dict = win.abconvertPriceCartTransformTestDict;
    if (!dict || typeof dict !== 'object') return null;

    const entry = dict[productId];
    if (!entry) return null;

    const experimentId = entry.experimentId;
    if (!experimentId) return null;

    const groupIndex = normaliseGroupIndex(groups[experimentId]);
    if (groupIndex == null) return null;

    const byGroup = entry.variantToGroupIndexToPrice?.[variantId];
    if (!byGroup) return null;

    const data = byGroup[groupIndex];
    if (!data) return null;

    // ── CURRENCY SAFETY ──────────────────────────────────────────────────
    //
    // data.price is in the shop's PRIMARY currency. Per-market prices are under
    // countryMap, keyed by country.
    //
    // So for a shopper on a non-primary market, the top-level price is simply a
    // different currency from the one we are about to format it as. Rendering it
    // produces ₱23.99 where the real price is ₱1,400.00 — an eighty-times-cheaper
    // number that a shopper would very reasonably expect to be charged.
    //
    // Hence: use countryMap when it covers this shopper, and otherwise write
    // nothing at all unless the displayed currency IS the primary currency.
    // Leaving Liquid's correct price alone is always better than replacing it
    // with a confidently wrong one.
    const scoped = config.country ? data.countryMap?.[config.country] : null;
    if (scoped) return scoped;

    if (config.currency !== config.primaryCurrency) return null;

    return data;
  };

  /* ----------------------------------------------------------- formatting */

  /**
   * @param {string | number | null | undefined} value - A decimal amount from their payload, e.g. '23.99'.
   * @returns {string | null} Formatted to match the `| money` filter, or null if unparseable.
   */
  const toMoney = (value) => {
    if (!config || value == null) return null;
    const asString = typeof value === 'number' ? String(value) : value;
    if (typeof asString !== 'string') return null;

    const minor = convertMoneyToMinorUnits(asString, config.currency);
    // Non-positive is rejected, not rendered. Their payload uses 0 and "" for
    // "no compare-at price", and formatMoney would happily turn 0 into "$0.00"
    // — a struck-through zero, or worse a headline price of nothing.
    if (minor == null || !Number.isFinite(minor) || minor <= 0) return null;

    return formatMoney(minor, config.moneyFormat, config.currency);
  };

  /* -------------------------------------------------------------- writing */

  /**
   * Replace the price text WITHOUT touching the element's children.
   *
   * These spans wrap a visually-hidden label — "Sale price", "Regular price" —
   * that only screen readers see. textContent = newPrice would delete it, so a
   * screen reader would read two bare numbers with no idea which is which.
   * price.liquid already carries a comment about a sold-out item announcing
   * "Sale price 400, Regular price 700"; do not undo that here.
   *
   * Returns true only if something actually changed — the MutationObserver
   * below depends on that to avoid reacting to its own writes forever.
   */
  /**
   * @param {Element} el - A .ab-price or .ab-compare-price element.
   * @param {string} text - The formatted price to show.
   * @returns {boolean} Whether anything actually changed.
   */
  const setPriceText = (el, text) => {
    const nodes = [...el.childNodes].filter(
      (node) => node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== ''
    );

    if (nodes.length === 0) {
      el.append(document.createTextNode(text));
      return true;
    }

    const first = nodes[0];
    if (!first) return false;
    const current = first.textContent ?? '';
    const leading = current.match(/^\s*/)?.[0] ?? '';
    const trailing = current.match(/\s*$/)?.[0] ?? '';
    const next = leading + text + trailing;

    let changed = false;
    if (first.textContent !== next) {
      first.textContent = next;
      changed = true;
    }
    // A second meaningful text node would mean markup we did not anticipate.
    // Blanking it is how we avoid leaving "$23.99 $21.99" on the page.
    for (const node of nodes.slice(1)) {
      if (node.textContent !== '') {
        node.textContent = '';
        changed = true;
      }
    }
    return changed;
  };

  /**
   * @param {HTMLElement} container - An element carrying data-ab-product-id and data-ab-variant-id.
   * @param {Record<string, unknown>} groups
   * @returns {boolean} Whether anything actually changed.
   */
  const applyToContainer = (container, groups) => {
    const productId = container.dataset.abProductId;
    const variantId = container.dataset.abVariantId;
    if (!productId || !variantId) return false;

    const data = priceFor(productId, variantId, groups);
    if (!data) return false;

    let changed = false;

    const price = toMoney(data.price);
    if (price) {
      for (const el of container.querySelectorAll('.ab-price')) {
        if (!(el instanceof HTMLElement)) continue;
        if (el.dataset.abRange === 'true') continue;
        if (setPriceText(el, price)) changed = true;
      }
    }

    // COMPARE-AT: write one when we have a real amount, and otherwise leave
    // Liquid's alone. Never hide it.
    //
    // Their guide documents compareAtPrice as optional and shows null and 0.
    // The live payload also uses "" (this shop, group 1: {compareAtPrice: "",
    // price: "29.99"}), which is undocumented and much more likely to mean
    // "left blank in their UI" than "delete the strikethrough".
    //
    // Guessing wrong in the hiding direction destroys something true: the
    // product's compare-at really is $44.99, and $44.99 struck beside a $29.99
    // test price is a genuine discount, not an invented one. Compare-at also
    // affects no charge, so leaving it cannot misquote anyone. An unparseable
    // value therefore means "no opinion".
    const compare = toMoney(data.compareAtPrice);
    if (compare) {
      for (const el of container.querySelectorAll('.ab-compare-price')) {
        if (!(el instanceof HTMLElement)) continue;
        if (setPriceText(el, compare)) changed = true;
      }
    }

    return changed;
  };

  /** @returns {boolean} Whether any price on the page changed. */
  const apply = () => {
    if (!config) return false;
    const groups = readGroups();
    if (!groups) return false;

    let changed = false;
    for (const container of document.querySelectorAll('[data-ab-variant-id]')) {
      if (!(container instanceof HTMLElement)) continue;
      if (applyToContainer(container, groups)) changed = true;
    }
    return changed;
  };

  /* ------------------------------------------------------------ scheduling */

  /**
   * Two independent reasons a price can be stale, and neither gives us an
   * event to listen to:
   *
   *  1. THEIR SCRIPT LOADS AFTER OURS. abconvertPriceCartTransformTestDict is
   *     populated by an async app script with no ready event.
   *
   *  2. THE THEME RE-RENDERS SERVER-SIDE. assets/section-renderer.js dispatches
   *     nothing (checked), so a variant switch, a filter, a sort or a
   *     pagination click silently replaces the markup with freshly-rendered
   *     Liquid prices. This is the case that matters most here, because a
   *     Single-vs-Kit test has shoppers switching variants constantly.
   *
   * A MutationObserver covers both, and covers quick-add and Rebuy widgets for
   * free. Polling covers the gap before the first mutation.
   */
  /** @type {number | null} */
  let frame = null;
  const schedule = () => {
    if (frame != null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      apply();
    });
  };

  const observe = () => {
    const observer = new MutationObserver((records) => {
      // Ignore our own writes. Without this the observer re-fires on every
      // character it just wrote. apply() is idempotent, so this is belt and
      // braces rather than the only guard — but it keeps the page off the
      // main thread in a loop on big collection grids.
      const relevant = records.some(
        (record) => record.type === 'childList' && record.addedNodes.length > 0
      );
      if (relevant) schedule();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  /**
   * Bounded retry for case 1. Stops at the first success or after ~10s rather
   * than polling forever — if their object has not appeared by then, no test is
   * running on this page and there is nothing to wait for.
   */
  const pollForTheirScript = () => {
    let attempts = 0;
    const tick = () => {
      attempts += 1;
      if (apply() || attempts >= 20) return;
      win.setTimeout(tick, 500);
    };
    tick();
  };

  const init = () => {
    if (!config) return;
    pollForTheirScript();
    observe();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /** Exposed for QA: crewcupAbPriceDisplay.debug() in the console. */
  win.crewcupAbPriceDisplay = {
    apply,
    debug: () => ({
      config,
      groups: readGroups(),
      dictPresent: Boolean(win.abconvertPriceCartTransformTestDict),
      dictProductIds: Object.keys(win.abconvertPriceCartTransformTestDict ?? {}),
      containers: [...document.querySelectorAll('[data-ab-variant-id]')]
        .filter(
          /** @returns {el is HTMLElement} */
          (el) => el instanceof HTMLElement
        )
        .map((el) => ({
          product: el.dataset.abProductId,
          variant: el.dataset.abVariantId,
          text: (el.textContent ?? '').replace(/\s+/g, ' ').trim(),
        })),
    }),
  };
}
